// Minimal authenticated client for the `/v1` API. One function — attach the
// bearer, parse JSON, throw on non-2xx so TanStack Query surfaces the failure.
// No SDK, no per-route wrappers (YAGNI): callers pass the path + their token.

import { problemExtensions } from '@developerz/domain';
import type { ZodType } from 'zod';
import { ApiError, parseBody } from './api-errors';
import { MOCKS, mockFetch } from './mocks';
import { orgFromPath } from './org';
import { markSessionExpired } from './session-expiry';

/**
 * Base URL of the `/v1` API. Override with `PUBLIC_API_URL` (see vite-env.d.ts).
 * Empty default = same-origin: in dev the vite proxy forwards `/v1` + `/auth`
 * to the local API, so a fresh clone works with zero env and zero CORS (the
 * old `http://localhost:3001` default sent a bearer cross-origin from :5173
 * and died on preflight unless DEV_CORS_ORIGIN was set — #354). Prod builds
 * set the real URL at build time.
 */
export const API_BASE_URL: string = import.meta.env['PUBLIC_API_URL'] ?? '';

export {
  ApiError,
  ApiSchemaError,
  errorReason,
  isForbidden,
  isUnauthorized,
  UserFacingError,
} from './api-errors';

/**
 * `scope: 'session'` asks as the SESSION — no `X-Dz-Account` claim, whatever
 * org the page is on — so the API answers under the session's own account. The
 * one caller is the identity probe (`useSessionMe`, lib/session.ts): who is
 * signed in does not change with the URL org, and a refused org claim (403) must
 * not read as "we could not tell who you are".
 */
export type ApiFetchScope = {
  readonly scope?: 'session';
  /**
   * `'operator'`: `token` is a short-lived operator credential, not the session
   * (`withOperatorCredential`, `lib/operator-credential.ts`, the one caller). A
   * 401 then says that CREDENTIAL is gone — revoked, or aged on a fast clock —
   * while the cookie session that minted it is still good, so it must not latch
   * the app to <Login/>. The caller drops the credential and retries once.
   */
  readonly credential?: 'operator';
};

/**
 * Authenticated `fetch` against the API. Always sends credentials so the
 * httpOnly `dz_session` cookie (prod login) rides along, and attaches
 * `Authorization: Bearer …` ONLY for a real `dev_pat_` token (dev) or an operator
 * credential (`credential: 'operator'`, whatever its prefix). A 401 is
 * surfaced as `ApiError` and the dashboard treats it as "signed out". Throws
 * `ApiError` on any non-2xx; returns the parsed JSON body otherwise.
 */
export async function apiFetch<S extends ZodType>(
  path: string,
  token: string | null,
  init: RequestInit | undefined,
  opts: { readonly schema: S },
): Promise<ReturnType<S['parse']>>;
export async function apiFetch<T>(
  path: string,
  token: string | null,
  init?: RequestInit,
  opts?: ApiFetchScope,
): Promise<T>;
export async function apiFetch(
  path: string,
  token: string | null,
  init?: RequestInit,
  opts?: { readonly schema?: ZodType } & ApiFetchScope,
): Promise<unknown> {
  const schema = opts?.schema === undefined ? undefined : { schema: opts.schema };
  // Dev-only: serve sample data when PUBLIC_MOCKS=1 (no API needed). Stripped
  // from prod builds — `MOCKS` is `false` there, so this branch + the import
  // dead-code-eliminate.
  //
  // The schema applies HERE TOO, on purpose. `lib/mocks.ts` is typed against the
  // client's mirror, so when the mirror was wrong the fixture agreed with the
  // wrong side and the Playwright lane was green over the same defect. Parsing
  // the mock through the same schema as the wire is what stops a fixture from
  // being the one shape nothing checks.
  if (MOCKS) return parseBody(await mockFetch<unknown>(path, init), path, init, undefined, schema);

  // Client-minted correlation id: sent as `x-request-id` and attached to any
  // ApiError so the error UI can show a copyable reference for support.
  const requestId = crypto.randomUUID();
  // URL-derived org scoping (#460): on an `/org/<login>/…` page every request
  // names that org via `X-Dz-Account`; the API membership-fences it and makes
  // it the effective tenant. Read from `location` at request time (not a
  // signal) so the header always matches the page issuing the request.
  const urlOrg =
    opts?.scope === 'session' || typeof window === 'undefined'
      ? null
      : orgFromPath(window.location.pathname);
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    // Send the session cookie cross-origin (dashboard → api). The API echoes
    // the exact origin + Allow-Credentials, so this is not a `*` request.
    credentials: 'include',
    headers: {
      // Only a genuine PAT becomes a bearer — the prod cookie marker never does.
      // An operator credential is ALWAYS the bearer: a prefix test here would
      // silently fall back to the cookie (and a 403) the day its prefix changed.
      ...(token !== null && (opts?.credential === 'operator' || token.startsWith('dev_pat_'))
        ? { Authorization: `Bearer ${token}` }
        : {}),
      ...(urlOrg !== null ? { 'x-dz-account': urlOrg } : {}),
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      // Last so a caller's `headers` can't clobber the minted id — the value
      // must match the `x-request-id` the server logs and the one on `ApiError`.
      ...init?.headers,
      'x-request-id': requestId,
    },
  });

  if (!res.ok) {
    // A 401 is the app's session dying, wherever it surfaces — so the latch is
    // set HERE, at the one client every `/v1` call goes through, rather than in
    // a `QueryCache({onError})` that would miss the hand-rolled callers
    // (webhook test-send, issue actions, the admin MCP console). `<App/>` reads
    // the latch and swaps to <Login/>; see lib/session-expiry.ts. The one
    // exception is a write on an operator credential (`credential: 'operator'`):
    // its 401 is about that credential, not the session.
    if (res.status === 401 && opts?.credential !== 'operator') markSessionExpired();
    // Best-effort peek at a problem-details body: its `detail` names the reason
    // (e.g. why live issuer discovery rejected a `PUT /v1/org-sso` config) and
    // its extension members carry the same reason as data (#1100).
    // why the cast: unvalidated error body — only the `detail` string is probed.
    const problem = (await res.json().catch(() => null)) as { detail?: unknown } | null;
    throw new ApiError(
      res.status,
      `${init?.method ?? 'GET'} ${path} → ${res.status}`,
      requestId,
      typeof problem?.detail === 'string' ? problem.detail : undefined,
      problemExtensions(problem),
    );
  }
  // No-body success (e.g. a `DELETE … → 204`): `res.json()` would throw on the
  // empty payload, so short-circuit. Callers type these as `apiFetch<void>`.
  const body: unknown = res.status === 204 ? undefined : await res.json();
  return parseBody(body, path, init, requestId, schema, res.status);
}

/**
 * `path` at an opaque `cursor` — page one is the bare path, so a mock or a proxy
 * matching the collection's exact URL still hits. Every paged lib builds its
 * request through this, rather than restating the join per endpoint.
 */
export function withCursor(path: string, cursor: string | null): string {
  if (cursor === null) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}cursor=${encodeURIComponent(cursor)}`;
}
